
import { DefectModel, DefectStationModel, QueryOperator } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { defectsStore, defectStationsStore } from '@/libs/defects/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import DefectSelectDialog from './DefectSelectDialog.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({
    components: { DefectSelectDialog },
})
export default class StationDefectsList extends Page {
    @Prop() stationId!: string;
    @Prop({ default: false }) compact!: boolean;
    @Ref() selectDialog!: any;

    loading = false;

    mounted() {
        this.loadData();
    }

    loadData() {
        this.loading = true;
        defectStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(undefined, [{ field: 'stationId', op: QueryOperator.Eq, comparand: this.stationId }])
        );
        this.subscribe(defectStationsStore, CrudReponse.GetAll)
            .then(() => {
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
            });
    }

    get defectStations(): DefectStationModel[] {
        const items = (defectStationsStore.useGetter(CrudGetter.Data) as DefectStationModel[]) ?? [];
        return items.filter((x) => x.stationId === this.stationId);
    }

    get sortedDefectStations(): DefectStationModel[] {
        return [...this.defectStations].sort((a, b) => {
            const defectA = this.getDefect(a.defectId);
            const defectB = this.getDefect(b.defectId);
            return (defectA?.code ?? '').localeCompare(defectB?.code ?? '');
        });
    }

    get allDefects(): DefectModel[] {
        return (defectsStore.useGetter(CrudGetter.Data) as DefectModel[]) ?? [];
    }

    getDefect(defectId: string | undefined): DefectModel | undefined {
        if (!defectId) return undefined;
        return this.allDefects.find((x) => x.id === defectId);
    }

    get currentDefectIds(): string[] {
        return this.defectStations.map(ds => ds.defectId!);
    }

    openSelectDialog() {
        this.selectDialog.open(this.currentDefectIds);
    }

    onDefectsSelected(selectedDefects: DefectModel[]) {
        const selectedIds = selectedDefects.map(d => d.id!);
        const currentIds = this.currentDefectIds;

        // Najít nově přidané
        const toAdd = selectedDefects.filter(d => !currentIds.includes(d.id!));
        
        // Najít odebrané
        const toRemove = this.defectStations.filter(ds => !selectedIds.includes(ds.defectId!));

        const promises: Promise<any>[] = [];

        // Přidat nové poruchy
        toAdd.forEach((defect) => {
            const defectStation: DefectStationModel = {
                id: Guid.EmptyGuid(),
                defectId: defect.id,
                stationId: this.stationId,
            };
            const promise = new Promise((resolve, reject) => {
                defectStationsStore.dispatch(CrudAction.Create, { item: defectStation });
                this.subscribe(defectStationsStore, CrudReponse.Create)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        // Odebrat poruchy
        toRemove.forEach((defectStation) => {
            const promise = new Promise((resolve, reject) => {
                defectStationsStore.dispatch(CrudAction.Delete, { id: defectStation.id });
                this.subscribe(defectStationsStore, CrudReponse.Delete)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        Promise.all(promises).then(() => {
            this.loadData();
        });
    }
}
